import { morphoTypeMarkets } from '@1delta/data-sdk'
import { assetsPerShareAccumulator } from '../accumulator'
import { formatEther, zeroAddress } from 'viem'
import { decodeMarkets, Market } from './utils/evmParser'
import { AdditionalYields } from '../../../types'
import { GenericTokenList } from '../../../types'
import {
  formatNr,
  parseLtv,
  liquidationPenaltyFromLltv,
} from './utils/parsers'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { MathLib } from './utils/mathLib'
import { apyToApr } from '../../user-data/utils'
import { formatMorphoPrice } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { MARKET_CHUNK_SIZE } from './publicCallBuild'

export function getMorphoMarketDataConverter(
  lender: string,
  chainId: string,
  prices: any,
  additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
  marketsOverride?: string[],
): [(data: any[]) => any | undefined, number] {
  const markets =
    marketsOverride ?? morphoTypeMarkets()?.MORPHO_BLUE?.[chainId] ?? []
  return [
    (rData: any[]) => {
      // Decode all chunks and concatenate
      let decoded: Market[] = []
      for (let j = 0; j < rData.length; j++) {
        const chunkDecoded = decodeMarkets(rData[j] ?? '0x') as Market[]
        decoded.push(...chunkDecoded)
      }

      const data: { [m: string]: MorphoGeneralPublicResponse } = {}
      decoded.forEach((market, i) => {
        const uniqueKey = markets[i]
        const { lltv, irm, oracle, loanToken, collateralToken, ...state } =
          market
        if (
          collateralToken &&
          loanToken &&
          oracle &&
          collateralToken !== zeroAddress &&
          loanToken !== zeroAddress
        ) {
          const m = 'MORPHO_BLUE_' + uniqueKey.slice(2).toUpperCase()
          // @ts-ignore
          if (!data[m]) data[m] = { data: {} }

          // Market-level risk: lltv (0..1) drives the Morpho liquidation
          // incentive. The same penalty applies to the seized collateral, so
          // both config entries below carry it. Morpho permits full
          // liquidation, hence closeFactor = 1.
          const ltv = parseLtv(lltv)
          const liquidationPenalty = liquidationPenaltyFromLltv(ltv)

          // 1. LOAN asset entry
          const loanRewards: any[] = []

          // get assets from list
          const loanAsset = tokens[loanToken.toLowerCase()]
          const collateralAsset = tokens[collateralToken.toLowerCase()]

          const loanDecimals = loanAsset?.decimals ?? 18
          const collateralDecimals = collateralAsset?.decimals ?? 18

          const loanKey =
            toOracleKey(loanAsset?.assetGroup!) ??
            toGenericPriceKey(loanAsset?.address ?? loanToken, chainId)

          let loanPrice = prices[loanKey]

          // try to infer loan price from collateral price if any
          if (!loanPrice) {
            const collateralKey =
              toOracleKey(collateralAsset?.assetGroup!) ??
              toGenericPriceKey(
                collateralAsset?.address ?? collateralToken,
                chainId,
              )
            const collateralPrice = prices[collateralKey]
            // get debt price by collateral price
            if (collateralPrice) {
              const priceDebtToCollateral = formatMorphoPrice(
                state.price.toString(),
                loanDecimals,
                collateralDecimals,
              )
              loanPrice = collateralPrice / priceDebtToCollateral
            }
          }

          const loanTokenAddress = (
            loanAsset?.address ?? loanToken
          ).toLowerCase()

          const meta = tokens[loanTokenAddress] ?? loanAsset
          const totalDeposits = formatNr(state.totalSupplyAssets, loanDecimals)
          const totalDebt = formatNr(state.totalBorrowAssets, loanDecimals)

          const utilization = MathLib.getUtilization({
            totalBorrowAssets: state.totalBorrowAssets,
            totalSupplyAssets: state.totalSupplyAssets,
          })

          let borrowApy = 0n
          let supplyApy = 0n

          try {
            borrowApy = MathLib.getBorrowApy(
              state.rateAtTarget,
              utilization,
              undefined,
              Number(state.lastUpdate),
            )
            supplyApy = MathLib.getSupplyApy(borrowApy, utilization, state.fee)
          } catch {}
          const borrowApr = apyToApr(Number(formatEther(borrowApy))) * 100
          const depositApr = apyToApr(Number(formatEther(supplyApy))) * 100

          const loanMarketUid = createMarketUid(chainId, m, loanTokenAddress)
          data[m].data[loanMarketUid] = {
            marketUid: loanMarketUid,
            name: 'Loan ' + (meta?.symbol ?? ''),
            poolId: loanTokenAddress,
            underlying: loanTokenAddress,
            asset: meta,
            totalDeposits,
            totalDebtStable: 0,
            totalDebt,
            totalDepositsUSD: totalDeposits * loanPrice,
            totalDebtStableUSD: 0,
            totalLiquidity: totalDeposits - totalDebt,
            borrowLiquidity: totalDeposits - totalDebt,
            totalLiquidityUSD: (totalDeposits - totalDebt) * loanPrice,
            borrowLiquidityUSD: (totalDeposits - totalDebt) * loanPrice,
            totalDebtUSD: totalDebt * loanPrice,
            // utilization is a WAD bigint from MathLib; surface as 0..1 number
            utilization: Number(utilization) / 1e18,
            accumulator: assetsPerShareAccumulator(
              state.totalSupplyAssets,
              state.totalSupplyShares,
              state.totalBorrowAssets,
              state.totalBorrowShares,
            ),
            depositRate: depositApr,
            variableBorrowRate: borrowApr,
            stableBorrowRate: 0,
            intrinsicYield:
              additionalYields?.intrinsicYields?.[meta?.assetGroup] ?? 0,
            rewards: loanRewards.length > 0 ? loanRewards : undefined,
            decimals: loanDecimals,
            config: {
              0: {
                category: 0,
                borrowCollateralFactor: 0,
                collateralFactor: 0,
                borrowFactor: 1,
                liquidationPenalty,
                closeFactor: 1,
                collateralDisabled: true,
                debtDisabled: false,
              },
            },
            closeFactor: 1,
            collateralActive: false,
            borrowingEnabled: true,
            depositsEnabled: true,
            hasStable: false,
            isActive: true,
            isFrozen: false,
          }

          const collateralAssetAddress = (
            collateralAsset?.address ?? collateralToken
          ).toLowerCase()
          const metaCollateral =
            tokens[collateralAssetAddress] ?? collateralAsset
          // 2. COLLATERAL asset entry
          const collateralMarketUid = createMarketUid(
            chainId,
            m,
            collateralAssetAddress,
          )
          data[m].data[collateralMarketUid] = {
            marketUid: collateralMarketUid,
            name: 'Collateral ' + (metaCollateral?.symbol ?? ''),
            poolId: collateralAssetAddress,
            underlying: collateralAssetAddress,
            asset: metaCollateral,
            totalDeposits: 0, // we do not have these metrics when querying on-chain
            totalDebtStable: 0,
            totalDebt: 0,
            totalLiquidity: 0,
            borrowLiquidity: 0,
            totalLiquidityUSD: 0,
            borrowLiquidityUSD: 0,
            totalDepositsUSD: 0,
            totalDebtStableUSD: 0,
            totalDebtUSD: 0,
            utilization: 0,
            depositRate: 0,
            variableBorrowRate: 0,
            stableBorrowRate: 0,
            intrinsicYield:
              additionalYields?.intrinsicYields?.[metaCollateral?.assetGroup] ??
              0,
            rewards: undefined,
            decimals: collateralDecimals,
            config: {
              0: {
                category: 0,
                borrowCollateralFactor: ltv,
                collateralFactor: ltv,
                borrowFactor: 1,
                liquidationPenalty,
                closeFactor: 1,
                collateralDisabled: false,
                debtDisabled: true,
              },
            },
            closeFactor: 1,
            collateralActive: true,
            borrowingEnabled: false,
            depositsEnabled: true,
            hasStable: false,
            isActive: true,
            isFrozen: false,
          }

          data[m].params = {
            market: {
              lender: m,
              collateralDecimals: collateralDecimals,
              loanDecimals: loanDecimals,
              id: uniqueKey,
              lltv: lltv.toString(),
              oracle: oracle,
              irm: irm,
              collateralAddress: collateralAssetAddress,
              loanAddress: loanTokenAddress,
              fee: state.fee.toString(),
              rateAtTarget: state.rateAtTarget.toString(),
            },
          }
          data[m].chainId = chainId
        }
      })

      return data
    },
    markets.length > 0 ? Math.ceil(markets.length / MARKET_CHUNK_SIZE) : 0,
  ]
}
